import { useCallback, useRef } from "react"

/**
 * Tells a monitoring data hook whether the fetch it is about to start is an automatic refresh.
 *
 * Call `beginFetch(refreshKey)` immediately before each fetch. A fetch started because `refreshKey` changed is a
 * background refresh; any other fetch (first load, a new range or granularity) is a foreground load.
 * `isBackground()` reports the kind of the latest fetch, so the hook can keep showing the previous data and
 * suppress `loading` while a background refresh is in flight.
 */
export function useBackgroundRefresh() {
  const lastRefreshKey = useRef<number | undefined>(undefined)
  const started = useRef(false)
  const background = useRef(false)

  const beginFetch = useCallback((refreshKey: number | undefined) => {
    background.current = started.current && refreshKey !== lastRefreshKey.current
    lastRefreshKey.current = refreshKey
    started.current = true
  }, [])

  const isBackground = useCallback(() => background.current, [])

  return { beginFetch, isBackground }
}
